import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { FC, ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import { TableContent, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { DQCascadeRunResource, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';

import { Body, StyledTable, Toolbar } from './Parts';
import {
  RUNNING_REFETCH_INTERVAL,
  RunStatusBadge,
  dqCascadeQueryKeys,
  formatDate,
  formatRunWindow,
} from './helpers';

const Actions = styled.div`
  display: inline-flex;
  gap: 8px;
`;

const Counts = styled.div`
  display: flex;
  gap: 12px;
  white-space: nowrap;
`;

interface Props {
  controls: ReactNode;
  groupNumber: number;
  onShowItems: (run: DQCascadeRunResource) => void;
}

const RunsTable: FC<Props> = ({ controls, groupNumber, onShowItems }) => {
  const [retryingRunId, setRetryingRunId] = useState<number | null>(null);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const fetchRuns = ({ pageParam = '/v1/editorial/queue/dq-cascade/runs' }) =>
    api.get<never, PaginatedResponse<DQCascadeRunResource>>(pageParam, {
      params: {
        'filter[groupNumber]': groupNumber,
      },
    });

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(dqCascadeQueryKeys.runs({ groupNumber }), fetchRuns, {
    getNextPageParam: (lastPage) => lastPage.links.next,
    refetchInterval: (data) =>
      data?.pages.some((page) =>
        page.data.some((run) => run.status === 'running'),
      )
        ? RUNNING_REFETCH_INTERVAL
        : false,
  });

  const runs = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const onRetryFailed = async (run: DQCascadeRunResource) => {
    try {
      const confirmed = await alerts.confirm(
        `Retry ${run.itemCounts.failed} failed item(s)?`,
        `${run.groupName} run of ${formatDate(run.runDate)}`,
      );

      if (!confirmed) return;

      setRetryingRunId(run.id);

      const { retried } = await api.post<never, { retried: number }>(
        `/v1/editorial/queue/dq-cascade/runs/${run.id}/retry-failed`,
      );

      await queryClient.invalidateQueries(dqCascadeQueryKeys.all);

      alerts.success(`${retried} item(s) queued for retry`);
    } catch (_) {
      alerts.error();
    } finally {
      setRetryingRunId(null);
    }
  };

  return (
    <>
      <Toolbar>{controls}</Toolbar>
      <Body>
        <StyledTable>
          <thead>
            <tr>
              <Th>Run Date</Th>
              <Th>Posts Published</Th>
              <Th>Posts × Pages</Th>
              <Th>Results</Th>
              <Th>Status</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              colSpan={6}
              isLoading={isLoading}
              isError={isError}
              hasData={!!runs.length}
              emptyTableText="No runs yet"
            >
              {runs.map((run) => (
                <tr key={run.id}>
                  <Td>{formatDate(run.runDate)}</Td>
                  <Td>{formatRunWindow(run)}</Td>
                  <Td>
                    {run.postsCount} × {run.pagesCount}
                  </Td>
                  <Td>
                    <Counts>
                      <Text as="span" $type="smallBold" $color="success">
                        {run.itemCounts.inserted} inserted
                      </Text>
                      <Text as="span" $type="smallBold" $color="gray">
                        {run.itemCounts.skipped} skipped
                      </Text>
                      {run.itemCounts.failed > 0 && (
                        <Text as="span" $type="smallBold" $color="error">
                          {run.itemCounts.failed} failed
                        </Text>
                      )}
                      {run.itemCounts.open > 0 && (
                        <Text as="span" $type="smallBold" $color="warning">
                          {run.itemCounts.open} pending
                        </Text>
                      )}
                    </Counts>
                  </Td>
                  <Td>
                    <RunStatusBadge run={run} />
                  </Td>
                  <Td>
                    <Actions>
                      {run.itemCounts.failed > 0 && (
                        <Button
                          small
                          color="red"
                          loading={retryingRunId === run.id}
                          disabled={retryingRunId !== null}
                          onClick={() => onRetryFailed(run)}
                        >
                          Retry failed
                        </Button>
                      )}
                      <Button
                        small
                        color="secondary"
                        onClick={() => onShowItems(run)}
                      >
                        Items
                      </Button>
                    </Actions>
                  </Td>
                </tr>
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
        <InfiniteLoader
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </Body>
    </>
  );
};

export default RunsTable;
